import "../assets/qreader.css";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { axiosInstance } from "../utils/axios";
import { toast } from "react-toastify";
import { useMutation } from "@tanstack/react-query";
import useDialogStore from "../stores/useDialogStore";
import { useQueryClient } from "@tanstack/react-query";
import Button from "./Button";
import { CheckIcon } from "@heroicons/react/20/solid";

export default function VerifyDevicesManually() {
  const { closeDialog } = useDialogStore();
  const [response, setResponse] = useState(null);
  const query = useQueryClient();
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isValid },
  } = useForm({
    mode: "onChange",
    defaultValues: {
      invoiceNumber: "",
      uniqueIds: "",
    },
  });

  const watchedUniqueIds = watch("uniqueIds");

  // Convert textarea value to array of unique, trimmed, non-empty strings
  const uniqueIdsArray = watchedUniqueIds
    .split("\n")
    .map((id) => id.trim())
    .filter((id) => id.length > 0);

  const verifyDevicesMutation = useMutation({
    mutationFn: async ({ confirmed = false }) => {
      const { data: response } = await axiosInstance.put(
        `${window.REACT_APP_API_ENDPOINT}/device-statuses/verify-multiple-devices/all`,
        {
          unique_ids: uniqueIdsArray,
          invoice_number: watch("invoiceNumber"),
          confirmed,
        }
      );
      return response;
    },
    onSuccess: (response, variables) => {
      if (response) {
        if (variables.confirmed) {
          // If confirming, show success and close
          toast.success("Devices confirmed successfully!");
          closeDialog();
        } else {
          // If just checking, show results
          setResponse(response);
          query.invalidateQueries({ queryKey: ["reserved-devices"] });
          query.invalidateQueries({ queryKey: ["invoiceDetails"] });
          query.invalidateQueries({ queryKey: ["soldInvoiceDetails"] });
          query.invalidateQueries({ queryKey: ["sold-devices"] });
        }
      }
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message || "Failed to verify devices");
    },
  });

  const onSubmit = (data) => {
    if (uniqueIdsArray.length === 0) {
      toast.warning("Please enter at least one unique ID");
      return;
    }
    verifyDevicesMutation.mutate({ confirmed: false });
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("Text");
    const currentValue = watch("uniqueIds");
    const newValue = currentValue
      ? currentValue + "\n" + pastedData.replace(/\r/g, "")
      : pastedData.replace(/\r/g, "");
    setValue("uniqueIds", newValue);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      const currentValue = watch("uniqueIds");
      const newValue = currentValue ? currentValue + "\n" : "\n";
      setValue("uniqueIds", newValue);
    }
  };

  const copyArrayToClipboard = async (array, arrayName) => {
    if (array && array.length > 0) {
      try {
        await navigator.clipboard.writeText(array.join("\n"));
        toast.success(`${arrayName} copied to clipboard!`);
      } catch (error) {
        // Fallback for older browsers
        const textArea = document.createElement("textarea");
        textArea.value = array.join("\n");
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand("copy");
        document.body.removeChild(textArea);
        toast.success(`${arrayName} copied to clipboard!`);
      }
    } else {
      toast.warning(`No ${arrayName} to copy`);
    }
  };

  // Reusable Device Status Card Component
  const DeviceStatusCard = ({
    title,
    devices,
    bgColor,
    textColor,
    borderColor,
    copyButtonColor,
    copyButtonHoverColor,
  }) => (
    <div className="bg-white border rounded-lg p-4 shadow-sm">
      <div className="flex justify-between items-center mb-3">
        <h4 className="font-medium text-gray-700 text-sm">{title}</h4>
        <button
          type="button"
          onClick={() => copyArrayToClipboard(devices, title)}
          className={`${copyButtonColor} ${copyButtonHoverColor} text-white text-xs px-2 py-1 rounded flex items-center gap-1`}
          title={`Copy ${title}`}
        >
          <svg
            className="w-3 h-3"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
            />
          </svg>
          Copy
        </button>
      </div>
      <div
        className={` border rounded p-1 min-h-[60px] max-h-32 overflow-auto`}
      >
        {devices && devices.length > 0 ? (
          <div className="flex flex-wrap gap-1">
            {devices.map((id) => (
              <span
                key={id}
                className={`inline-block border ${bgColor} ${textColor} text-xs px-2 py-1 rounded-md`}
              >
                {id}
              </span>
            ))}
          </div>
        ) : (
          <span className="text-gray-400 text-xs">None</span>
        )}
      </div>
      <div className="mt-2 text-xs text-gray-500 text-center">
        {devices ? devices.length : 0} device
        {devices && devices.length !== 1 ? "s" : ""}
      </div>
    </div>
  );

  return (
    <div className="space-y-4">
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div>
          <label className="text-sm text-gray-700">
            Invoice Number <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            className={`w-full border rounded-md p-2 text-gray-900 placeholder:text-gray-400 text-sm ${
              errors.invoiceNumber ? "border-red-500" : "border-gray-300"
            }`}
            placeholder="Enter Invoice Number"
            {...register("invoiceNumber", {
              required: "Invoice number is required",
            })}
          />
          {errors.invoiceNumber && (
            <p className="text-red-500 text-xs mt-1">
              {errors.invoiceNumber.message}
            </p>
          )}
        </div>

        <div>
          <div className="text-sm text-gray-700 flex justify-between items-center">
            <label>
              Unique IDs <span className="text-red-500">*</span>
            </label>
            <button
              type="button"
              className=" bg-blue-100 hover:bg-blue-200 text-blue-700 text-xs px-2 py-1 rounded"
              onClick={() => {
                const value =
                  typeof watch === "function" ? watch("uniqueIds") : "";
                const cleaned = value
                  .split("\n")
                  .map((line) => line.trim())
                  .filter((line) => line !== "")
                  .join("\n");
                setValue("uniqueIds", cleaned, { shouldValidate: true });
              }}
              tabIndex={-1}
              title="Remove empty rows"
            >
              Clean Empty Rows
            </button>
          </div>
          <div className="relative">
            <textarea
              className={`w-full border rounded-md p-2 min-h-[100px] text-gray-900 placeholder:text-gray-400 text-sm ${
                errors.uniqueIds ? "border-red-500" : "border-gray-300"
              }`}
              placeholder="Type or paste and press Enter..."
              {...register("uniqueIds", {
                required: "At least one unique ID is required",
              })}
              onPaste={handlePaste}
              onKeyDown={handleKeyDown}
              autoFocus
            />
          </div>
          {errors.uniqueIds && (
            <p className="text-red-500 text-xs mt-1">
              {errors.uniqueIds.message}
            </p>
          )}
        </div>
        <span className="text-xs text-blue-500 font-bold">
          {uniqueIdsArray.length}
          {uniqueIdsArray.length > 1 ? " scanned devices" : " scanned device"}
        </span>

        <div className="text-xs text-gray-500">
          Paste a list of values or press Enter after each entry.
        </div>

        {response && (
          <div className="mt-4 border rounded-md p-4 bg-gray-50 text-sm text-gray-800 space-y-3">
            <div className="font-semibold text-base text-gray-700 flex justify-between items-center gap-2">
              {response.message}
              <span className="inline-block bg-green-100 text-green-700 px-2 py-0.5 rounded text-xs font-medium">
                Success
              </span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <div>
                <span className="font-medium text-gray-600">
                  Invoice Number:
                </span>{" "}
                <span className="text-gray-900 font-bold">
                  {response.invoice_number}
                </span>
              </div>

              <div>
                <span className="font-medium text-gray-600">
                  Total Devices Requested:
                </span>{" "}
                <span className="text-gray-900 font-bold">
                  {response.total_devices_requested}
                </span>
              </div>
            </div>
            <hr />
            {/* Device Status Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2  gap-4 mt-4">
              <DeviceStatusCard
                title="Devices not verified"
                devices={response.devices_in_invoice}
                bgColor="bg-blue-50"
                textColor="text-blue-800"
                borderColor="border-blue-200"
                copyButtonColor="bg-blue-600"
                copyButtonHoverColor="hover:bg-blue-700"
              />
              <DeviceStatusCard
                title="Already Verified"
                devices={response.devices_already_verified}
                bgColor="bg-green-50"
                textColor="text-green-800"
                borderColor="border-green-200"
                copyButtonColor="bg-green-600"
                copyButtonHoverColor="hover:bg-green-700"
              />

              <DeviceStatusCard
                title="Not in Invoice"
                devices={response.devices_not_in_invoice}
                bgColor="bg-yellow-50"
                textColor="text-yellow-800"
                borderColor="border-yellow-200"
                copyButtonColor="bg-yellow-600"
                copyButtonHoverColor="hover:bg-yellow-700"
              />

              <DeviceStatusCard
                title="Without Status"
                devices={response.devices_without_status}
                bgColor="bg-red-50"
                textColor="text-red-800"
                borderColor="border-red-200"
                copyButtonColor="bg-red-600"
                copyButtonHoverColor="hover:bg-red-700"
              />
              <div className="col-span-2">
                <DeviceStatusCard
                  title="Devices that need verification"
                  devices={response.devices_that_need_verification}
                  bgColor="bg-indigo-50"
                  textColor="text-indigo-800"
                  borderColor="border-indigo-200"
                  copyButtonColor="bg-indigo-600"
                  copyButtonHoverColor="hover:bg-indigo-700"
                />
              </div>
            </div>
            <div>
              <span className="font-medium text-gray-600">Devices Found:</span>{" "}
              <span className="text-gray-900 font-bold">
                {response.devices_found}
              </span>
            </div>
            <Button
              variant="success"
              icon={<CheckIcon className="w-4 h-4" />}
              onClick={() => verifyDevicesMutation.mutate({ confirmed: true })}
              className="w-full"
              disabled={
                verifyDevicesMutation.isPending ||
                !response.devices_in_invoice ||
                response.devices_in_invoice.length === 0
              }
            >
              {verifyDevicesMutation.isPending ? (
                <div className="flex items-center gap-2">
                  <div className="loader h-4 w-4 border-[2px] border-t-transparent border-white rounded-full animate-spin"></div>
                  Confirming...
                </div>
              ) : (
                "I want to verify the valid devices"
              )}
            </Button>
          </div>
        )}

        <div className="flex justify-end gap-3">
          <Button type="button" variant="secondary" onClick={closeDialog}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={
              uniqueIdsArray.length === 0 || verifyDevicesMutation.isPending
            }
          >
            {verifyDevicesMutation.isPending ? (
              <div className="flex items-center gap-2">
                <div className="loader h-4 w-4 border-[2px] border-t-transparent border-white rounded-full animate-spin"></div>
                Verifying...
              </div>
            ) : (
              "Verify Devices"
            )}
          </Button>
        </div>
      </form>
    </div>
  );
}
